import React, { Suspense, useEffect } from "react";
import { graphql, PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import List from "common/DS/List";
import { DoubleListItem } from "common/DS/ListItem";
import { OutlinedButton, TextButton } from "common/DS/Buttons";
import { HubComponentQuery, HubComponentQuery$data } from "__generated__/HubComponentQuery.graphql";
import Skeleton from "common/DS/Skeleton";
import { Body1, H6, InlineBody2 } from "common/DS/Text";
import HubMessagesList from "./HubMessagesList";

const query = graphql`
    query HubComponentQuery {
        me {
            id
            firstName
            companyName
        }
        availableCheckins {
            id
            displayTitle
            linkUrl
            daysLeft
        }
        nextSurvey {
            id
            displayTitle
            linkUrl
            daysLeft
        }
        checkinSummary {
            summaryText
            buttonText
        }
        hubMessages {
            type
            isRead
            timeCreated
            url
            title
            description
        }
    }
`;

const daysLeftText = (daysLeft: number | null | undefined): string | null => {
    if (daysLeft === null || daysLeft === undefined) return null;
    if (daysLeft === 0) return "Ends today";
    return `${daysLeft} day${daysLeft > 1 ? "s" : ""} left`;
};

const AllSurveysList = ({
    availableCheckins,
}: {
    availableCheckins: HubComponentQuery$data["availableCheckins"];
}) => (
    <List className="w100 pt-0">
        {availableCheckins.map((checkin) => (
            <DoubleListItem
                key={checkin.id}
                content={<strong>{checkin.displayTitle}</strong>}
                secondary={
                    <InlineBody2 className="color-navy-medium">
                        {daysLeftText(checkin.daysLeft)}
                    </InlineBody2>
                }
                className="pl-0"
            >
                <TextButton href={checkin.linkUrl}>Start</TextButton>
            </DoubleListItem>
        ))}
    </List>
);

const HubSurveySummary = ({
    summaryText,
    buttonText,
    availableCheckins,
    nextSurvey,
}: {
    summaryText: string;
    buttonText: string | null | undefined;
    availableCheckins: HubComponentQuery$data["availableCheckins"];
    nextSurvey: HubComponentQuery$data["nextSurvey"];
}) => {
    const [allSurveysVisible, setAllSurveysVisible] = React.useState(false);
    const seeMoreText =
        availableCheckins.length > 1 ? `See ${availableCheckins.length - 1} more` : null;

    return (
        <Body1 className="color-navy-medium">
            <p className="hub__surveys_summary">{summaryText}</p>
            {allSurveysVisible ? (
                <AllSurveysList availableCheckins={availableCheckins} />
            ) : (
                <div className="flex flex-row gap-md flex-jfe">
                    {seeMoreText && (
                        <TextButton
                            onClick={() => setAllSurveysVisible(true)}
                            className="pl-md pr-md"
                        >
                            {seeMoreText}
                        </TextButton>
                    )}
                    {buttonText && nextSurvey?.linkUrl && (
                        <OutlinedButton className="pl-md pr-md" href={nextSurvey.linkUrl}>
                            {buttonText}
                        </OutlinedButton>
                    )}
                </div>
            )}
        </Body1>
    );
};

const HubContent = ({ queryRef }: { queryRef: PreloadedQuery<HubComponentQuery> }) => {
    const { me, availableCheckins, nextSurvey, checkinSummary, hubMessages } = usePreloadedQuery(
        query,
        queryRef,
    );

    return (
        <div className="hub__content hub__two-point-o-content">
            <div className="hub__survey_section">
                <H6 className="color-navy-high">Welcome, {me.firstName}</H6>
                <HubSurveySummary
                    summaryText={checkinSummary.summaryText}
                    buttonText={checkinSummary.buttonText}
                    availableCheckins={availableCheckins}
                    nextSurvey={nextSurvey}
                />
            </div>
            <HubMessagesList messages={hubMessages} />
        </div>
    );
};

const HubComponent = () => {
    const [queryRef, loadQuery, disposeQuery] = useQueryLoader<HubComponentQuery>(query);

    useEffect(() => {
        loadQuery({});
        return () => {
            disposeQuery();
        };
    }, []);

    if (!queryRef) {
        return <Skeleton width="100%" height={200} />;
    }

    return (
        <Suspense fallback={<Skeleton width="100%" height={200} />}>
            <HubContent queryRef={queryRef} />
        </Suspense>
    );
};

export default HubComponent;
